import VueRouter from 'vue-router';
import { EventEmitter } from 'events';
import { mergeWith, merge, get, set, isString, template } from 'lodash';
import * as _ from 'lodash';
import debug from 'debug';
import { Vue } from 'vue-property-decorator';
import { FakeAuth, Auth } from './auth';
import { Connections } from './connections/index.js';
import { DocumentTitle } from './document-title';
import { fns } from './fn';
import { traverse } from './helpers';
import { I18n } from './i18n';
import { ObjectStore } from './object-store';
import { getRouter } from './routes';
import { Users } from './users';
import * as filters from './filters';
import { deepCopy } from './helpers';
import { Repository } from './repo';
import { initializeErrorModalOptionsHookHandler } from './errors';
const log = debug('itp:core:application'), vars = process.env.vars, env = process.env, AUTH_TOKEN = env.AUTH_TOKEN, NODE_ENV = env.NODE_ENV, TEMPLATE = `
  <div class="box-stretch">
    <router-view class="box-stretch"></router-view>
    <ItpModalHost></ItpModalHost>
  </div>`.trim();
log('process.env: %o', env);
log('vars?: %o', vars);
initializeErrorModalOptionsHookHandler();
export class Application {
    $vue;
    ready = false;
    document;
    vars;
    data;
    spec;
    template;
    auth;
    connections;
    documentTitle;
    i18n;
    users;
    fn;
    objectStore;
    config;
    events;
    repo;
    confirmPageClose = false;
    router;
    PACKAGE_VERSION = env.PACKAGE_VERSION;
    PRODUCT_VERSION = env.PRODUCT_VERSION;
    get vue() {
        return this.$vue;
    }
    constructor(app, template = TEMPLATE) {
        this.document = document;
        log('initializing app... %o +NNNms', app);
        processApplicationSpec(app, mergeWith({}, app.defaultVars, vars));
        this.events = new EventEmitter();
        set(window, '$app', this);
        for (let [k, v] of Object.entries(filters)) {
            Vue.filter(k, v);
        }
        this.vars = vars;
        this.i18n = new I18n({
            storageKey: app.name,
            translations: app.translations,
            supportedLocales: app.supportedLocales
        });
        this.i18n.registerFilters();
        this.documentTitle = new DocumentTitle('Integro', ' | Integro');
        this.objectStore = new ObjectStore(app.name);
        this.auth = this.createAuth();
        this.data = app.data ? { ...app.data } : {};
        this.spec = app;
        this.template = template;
        this.config = merge(deepCopy(CONFIGURATION), app.config || {});
        if (app.extensions) {
        }
        this.connections = new Connections(this.i18n.LCID);
        this.users = new Users(this);
        this.users.registerFilters();
        this.fn = fns(this);
        this.repo = new Repository(this.connections);
        Vue.config.errorHandler = (error, vm, info) => {
            try {
                if (this.$vue) {
                    console.error(error);
                    this.$vue.$root.$emit('app:unhandledError', { error });
                    return;
                }
                console.error(error);
            }
            catch (err) {
                console.error(err);
            }
        };
        window.addEventListener('beforeunload', e => {
            if (this.confirmPageClose) {
                this.onBeforeUnload(e);
            }
        });
    }
    toggleConfirmPageClose(event) {
        this.confirmPageClose = !this.confirmPageClose;
    }
    onBeforeUnload(e) {
        e.preventDefault();
        e.returnValue = '';
    }
    mount(selector = '#itp-app') {
        log('mounting app +NNNms');
        try {
            Promise.resolve()
                .then(() => this.auth.init())
                .then(() => this.createVue())
                .then(() => this.connections.initialize(this.spec.connections))
                .then(() => this.users.load())
                .then(() => {
                this.vue.$mount(selector);
                log(`${this.spec.name} mounted to ${selector}. %o +NNNms`, this);
                this.ready = true;
            })
                .catch(err => this.onInitError(err, selector));
        }
        catch (err) {
            this.onInitError(err, selector);
        }
    }
    get isFakeAuth() {
        return NODE_ENV === 'development' && AUTH_TOKEN;
    }
    createAuth() {
        if (AUTH_TOKEN && this.isFakeAuth) {
            log('Faking auth!');
            return new FakeAuth({
                token: AUTH_TOKEN,
                accountUrl: get(vars, 'auth.accountUrl')
            });
        }
        return new Auth(this.getAuthVars());
    }
    getAuthVars() {
        let result = get(vars, 'auth', {});
        const realm = this.getRealmFromHeaderMeta();
        if (realm) {
            result = set(result, 'realm', realm);
        }
        const url = this.getKeycloakURLFromHeaderMeta();
        if (url) {
            result = set(result, 'url', url);
        }
        return result;
    }
    getRealmFromHeaderMeta() {
        const el = document.querySelector('meta[name="kc-realm"]');
        if (el instanceof HTMLMetaElement) {
            return el.content;
        }
    }
    getKeycloakURLFromHeaderMeta() {
        const el = document.querySelector('meta[name="kc-url"]');
        if (el instanceof HTMLMetaElement) {
            return el.content;
        }
    }
    createVue() {
        Vue.use(VueRouter);
        const router = getRouter(this.spec.home);
        this.router = router;
        Vue.prototype._ = _;
        Vue.prototype.$app = this;
        Vue.prototype.$appContext = this;
        Vue.prototype.$config = this.config;
        Vue.prototype.$fn = this.fn;
        Vue.prototype.$i18n = this.i18n;
        Vue.prototype.$store = this.objectStore;
        this.$vue = new Vue({
            router,
            provide: { ctx: this },
            template: this.template,
            data: () => this.data
        });
    }
    onInitError(error, selector) {
        log('Init error: %o', error);
        new Vue({
            template: `
        <ItpApplicationInitError :error=error>
        </ItpApplicationInitError>
      `,
            data: () => ({ error })
        }).$mount(selector);
    }
    static configure(configuration) {
        log('configuring application: %o +NNNms', configuration);
        merge(CONFIGURATION, configuration);
    }
}
const CONFIGURATION = {};
function processApplicationSpec(app, vars) {
    function process(value, key, obj) {
        const _isString = isString(value);
        if (_isString && value.indexOf('<%=') > -1) {
            obj[key] = template(value)(vars);
            console.log('template created', key);
            return;
        }
    }
    console.log('traversing app...');
    traverse(app.connections, process, 1);
    console.log('traversing completed.');
    return app;
}
//# sourceMappingURL=application.js.map